// ! CRUD Operation
const OPERATION = Object.freeze({
  create: 'create', // * CREATE
  view: 'view', // * READ
  edit: 'edit', // * UPDATE
  remove: 'remove', // * DELETE
});

const ROUTES = Object.freeze({
  // * BASE
  base: '/',

  // * AUTH
  admin: '/admin',
  login: '/login',
  passwordReset: '/password-reset/:token',

  // * PROTECTED ROUTES
  users: 'users',
  userCreate: `${OPERATION.create}`,
  userEdit: `${OPERATION.edit}/:userId`,
  userView: `${OPERATION.view}/:userId`,

  companies: 'companies',
  companyCreate: `${OPERATION.create}`,
  companyEdit: `${OPERATION.edit}/:companyId`,
  companyView: `${OPERATION.view}/:companyId`,
  companyProfile: ':companyId',

  sectors: 'sectors',
  sectorCreate: `${OPERATION.create}`,
  sectorEdit: `${OPERATION.edit}/:sectorId`,
  sectorView: `${OPERATION.view}/:sectorId`,

  employees: 'employees',
  employeeCreate: `${OPERATION.create}`,
  employeeUpload: `upload`,
  employeeEdit: `${OPERATION.edit}/:employeeId`,
  employeeView: `${OPERATION.view}/:employeeId`,
  employeeProfile: ':employeeId',
  employeesLeavesOverview: `leaves-overview`,

  time_sheet: 'timesheet',
  time_sheetExport: 'export',
  time_sheetUpload: 'upload',
  time_sheetCalendar: `${OPERATION.edit}/:employeeId`,

  exportToExcel: 'export-to-excel',
  hrRequest: 'hr-requests',
  calculator: 'calculator',
  guidelines: 'guidelines',
  tools: 'tools',
  categories: 'categories',
  techAdmin: 'tech-admin',
  knowledgeBase: 'knowledge-base',

  // * Payroll Automation
  payrollAutomation: 'payroll-automation',

  // * Smart Parser
  smartParser: 'smart-parser',

  // * OZ (Obracun Zarada)
  oz: 'oz',

  // * Energy Tools (BESS analyzer)
  energyTools: 'energy-tools',

  // * Fleet (vozni park / leasing tracker)
  fleet: 'fleet',

  // * Financial Statements
  financialStatements: 'financial-statements',

  // * Group Reporting
  groupReporting: 'group-reporting',
  clientReports: 'client-reports',
  myReports: 'my-reports',
  compensation: 'compensation',
  taxReturns: 'tax-returns',
  suppliers: 'suppliers',
  supplierProfile: ':pib',
  sefState: 'sef-state',
  diReports: 'di-reports',
  teamAssignments: 'team-assignments',
  teamContacts: 'client-contacts',
  myContacts: 'my-contacts',

  // * Time Tracking
  timeTracking: 'time-tracking',
  timeTrackingProjects: 'projects',
  timeTrackingProjectView: `view/:projectId`,
  timeTrackingApproval: 'approval',
  timeTrackingReports: 'reports',

  // * Announcements
  announcements: 'announcements',

  // * Activity feed (company-wide audit trail)
  activity: 'activity',

  account: 'account',
  accountEdit: `${OPERATION.edit}/:userId`,

  contactUs: 'contact-us',

  supportTickets: 'support-tickets',
  supportTicketView: `${OPERATION.view}/:ticketId`,
  myTickets: 'my-tickets',

  // * Invoices
  invoicesCreate: 'create-invoices',

  // * Incoming Invoices
  incomingInvoices: 'incoming-invoices',
  incomingInvoiceView: `${OPERATION.view}/:invoiceId`,

  // * Outgoing Invoices
  outgoingInvoices: 'outgoing-invoices',
  outgoingInvoiceView: `${OPERATION.view}/:invoiceId`,
  outgoingInvoiceEdit: `${OPERATION.edit}/:invoiceId`,

  // * Customers Invoices
  customersInvoices: 'customers-invoices',
  customerInvoicesCreate: `${OPERATION.create}`,
  customerInvoicesEdit: `${OPERATION.edit}/:customerId`,
  customerInvoiceView: `${OPERATION.view}/:customerId`,

  productsInvoices: 'products-invoices',
  productCreate: `${OPERATION.create}`,
  productEdit: `${OPERATION.edit}/:productId`,
  productView: `${OPERATION.view}/:productId`,

  archiveInvoices: 'archive-invoices',
  archiveInvoiceView: `${OPERATION.view}/:invoiceId`,
  archiveInvoiceEdit: `${OPERATION.edit}/:invoiceId`,

  // * HAS ACCESS DENIED
  accessDenied: '/admin/access-denied',

  // ! CATCH
  notFound: '*',
});

const ROLES = Object.freeze({
  superAdmin: 'superAdmin',
  techAdmin: 'techAdmin',
  admin: 'admin',
  client: 'client',
  hrOfficer: 'hrOfficer',
  clientOfficer: 'clientOfficer',
  eInvoiceOfficer: 'eInvoiceOfficer',
  employee: 'employee',
  clientContact: 'clientContact',
  fleetOfficer: 'fleetOfficer',
  energyOfficer: 'energyOfficer',
  auditor: 'auditor',
  adminOfficer: 'adminOfficer',
});

const PERMISSIONS = Object.freeze({
  userView: 'user_view',
  userCreate: 'user_create',
  userEdit: 'user_edit',
  userRemove: 'user_remove',

  companyView: 'company_view',
  companyCreate: 'company_create',
  companyEdit: 'company_edit',
  companyRemove: 'company_remove',
  companyStatuaryDocumentsCreate: 'company_statuary_documents_create',

  sectorView: 'sector_view',
  sectorCreate: 'sector_create',
  sectorEdit: 'sector_edit',
  sectorRemove: 'sector_remove',

  employeeView: 'employee_view',
  employeeCreate: 'employee_create',
  employeeEdit: 'employee_edit',
  employeeRemove: 'employee_remove',

  time_sheetView: 'timesheet_view',
  time_sheetCreate: 'timesheet_create',
  time_sheetEdit: 'timesheet_edit',
  time_sheetRemove: 'timesheet_remove',

  excelView: 'excel_view',

  hrRequestView: 'request_view',
  hrRequestCreate: 'request_create',
  hrRequestEdit: 'request_edit',
  hrRequestRemove: 'request_remove',

  terminationCreate: 'termination_create',
  terminationRemove: 'termination_remove',

  calculatorView: 'calculator_view',

  guidelineView: 'guideline_view',
  guidelineCreate: 'guideline_create',

  eInvoiceView: 'eInvoice_view',
  eInvoiceCreate: 'eInvoice_create',
  eInvoiceEdit: 'eInvoice_edit',
  eInvoiceRemove: 'eInvoice_remove',

  invoiceView: 'invoice_view',
  invoiceCreate: 'invoice_create',
  invoiceEdit: 'invoice_edit',
  invoiceRemove: 'invoice_remove',

  regularInvoiceView: 'invoice_view',
  regularInvoiceCreate: 'invoice_create',
  regularInvoiceEdit: 'invoice_edit',
  regularInvoiceRemove: 'invoice_remove',

  productView: 'product_view',
  productCreate: 'product_create',
  productEdit: 'product_edit',
  productRemove: 'product_remove',

  categoryView: 'category_view',
  categoryCreate: 'category_create',
  categoryEdit: 'category_edit',
  categoryRemove: 'category_remove',

  customerView: 'customer_view',
  customerCreate: 'customer_create',
  customerEdit: 'customer_edit',
  customerRemove: 'customer_remove',

  supportView: 'support_view',
  supportCreate: 'support_create',
  supportEdit: 'support_edit',
  supportRemove: 'support_remove',

  techAdminView: 'tech_admin_view',
  techAdminCreate: 'tech_admin_create',

  payrollAutomationView: 'payroll_automation_view',
  payrollAutomationCreate: 'payroll_automation_create',
  payrollAutomationEdit: 'payroll_automation_edit',

  smartParserView: 'smart_parser_view',
  smartParserCreate: 'smart_parser_create',
  smartParserEdit: 'smart_parser_edit',

  timeTrackingView: 'time_tracking_view',
  timeTrackingCreate: 'time_tracking_create',
  timeTrackingEdit: 'time_tracking_edit',
  projectView: 'project_view',
  projectCreate: 'project_create',
  projectEdit: 'project_edit',

  financialStatementsView: 'financial_statements_view',
  financialStatementsCreate: 'financial_statements_create',
  financialStatementsEdit: 'financial_statements_edit',

  groupReportingView: 'group_reporting_view',
  groupReportingCreate: 'group_reporting_create',
  groupReportingEdit: 'group_reporting_edit',
  clientReportsView: 'client_reports_view',
  clientReportsEdit: 'client_reports_edit',

  announcementView: 'announcement_view',
  announcementCreate: 'announcement_create',
  announcementEdit: 'announcement_edit',
  announcementRemove: 'announcement_remove',
});

const ROLES_PERMISSIONS = Object.freeze({
  HOME: {
    VIEW: {
      requiredRoles: [],
      requiredPermissions: [],
      bypassAuthCheck: true,
    },
  },

  USERS: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.userView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.userCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.userEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.userRemove],
      bypassAuthCheck: false,
    },
  },

  COMPANIES: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.eInvoiceOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.companyView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.companyCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.companyEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.companyRemove],
      bypassAuthCheck: false,
    },
  },

  SECTORS: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.sectorView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.sectorCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.techAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
      ],
      requiredPermissions: [PERMISSIONS.sectorEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.sectorRemove],
      bypassAuthCheck: false,
    },
  },

  EMPLOYEES: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.employeeView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.employeeCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.employeeEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.employeeRemove],
      bypassAuthCheck: false,
    },
    EXPORT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.employeeView],
      bypassAuthCheck: false,
    },
  },

  TIME_SHEET: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.time_sheetView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.time_sheetCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.time_sheetEdit],
      bypassAuthCheck: false,
    },
    ONLY_VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.techAdmin,
      ],
      requiredPermissions: [
        PERMISSIONS.time_sheetCreate,
        PERMISSIONS.time_sheetEdit,
        PERMISSIONS.time_sheetRemove,
      ],
      bypassAuthCheck: false,
    },
  },

  REQUEST: {
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.hrRequestCreate],
      bypassAuthCheck: false,
    },
  },

  TERMINATE: {
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.terminationCreate],
      bypassAuthCheck: false,
    },
  },

  EXPORTS: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.excelView],
      bypassAuthCheck: false,
    },
  },

  HR_REQUEST: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.hrRequestView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.hrOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.hrRequestCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.hrOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.hrRequestEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.hrOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.hrRequestRemove],
      bypassAuthCheck: false,
    },
  },

  CALCULATOR: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.hrOfficer,
        ROLES.clientOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.calculatorView],
      bypassAuthCheck: false,
    },
  },

  GUIDELINES: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.hrOfficer,
        ROLES.clientOfficer,
        ROLES.eInvoiceOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.guidelineView],
      bypassAuthCheck: false,
    },

    CREATE: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.guidelineCreate],
      bypassAuthCheck: false,
    },

    EDIT: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.guidelineCreate],
      bypassAuthCheck: false,
    },

    REMOVE: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.guidelineCreate],
      bypassAuthCheck: false,
    },
  },

  CATEGORIES: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.categoryView],
      bypassAuthCheck: false,
    },
  },

  KNOWLEDGE_BASE: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [],
      bypassAuthCheck: false,
    },
  },

  TOOLS: {
    VIEW: {
      requiredRoles: [
        ROLES.techAdmin,
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.eInvoiceOfficer,
        ROLES.employee,
      ],
      requiredPermissions: [],
      bypassAuthCheck: false,
    },
  },

  // Mirrors who holds `activity_view` in
  // server-hlb/constants/permissionsConstant.js. Narrower than most: the feed
  // is a company-wide management view, so an employee (who would see
  // colleagues' records) and an hrOfficer (whose reach is a sector, not a
  // company) do not hold it by default. `auditor` is the way in for them —
  // worn on top of the role they already have, and granting the feed alone.
  ACTIVITY: {
    VIEW: {
      requiredRoles: [
        ROLES.techAdmin,
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.auditor,
      ],
      requiredPermissions: [],
      bypassAuthCheck: false,
    },
  },

  ENERGY_TOOLS: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin, ROLES.energyOfficer],
      requiredPermissions: [],
      bypassAuthCheck: false,
    },
  },

  SUPPLIERS: {
    // Exactly who sees the incoming invoices (INVOICES.VIEW); the server also
    // asks for that role IN the company, and for the ledger switch before
    // anything from the books.
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.invoiceView],
      bypassAuthCheck: false,
    },
  },
  SEF_STATE: {
    // „Podaci sa SEF-a": who sees the incoming invoices; the server also asks
    // for that role IN the company.
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.client, ROLES.eInvoiceOfficer, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.invoiceView],
      bypassAuthCheck: false,
    },
  },
  TAX_RETURNS: {
    // Whoever sees a company's invoices sees its tax returns.
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.techAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.eInvoiceOfficer,
        ROLES.adminOfficer,
      ],
      requiredPermissions: [],
      bypassAuthCheck: false,
    },
    // Importing, typing in and overruling a status is the accountants' job.
    MANAGE: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin, ROLES.admin],
      requiredPermissions: [],
      bypassAuthCheck: false,
    },
  },

  // HLB-internal: who from the team works on which client. Managers see all,
  // a staff member their own clients, an adminOfficer all without changing
  // anything; the server decides which.
  TEAM_ASSIGNMENTS: {
    // `employee` is every company's employee role: the login flag lets in only
    // the HLB team's own people, who get just their tasks on this page.
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.techAdmin,
        ROLES.admin,
        ROLES.adminOfficer,
        ROLES.employee,
      ],
      requiredPermissions: [],
      bypassAuthCheck: false,
      requiredCompanyFlag: 'hasTeamAssignments',
    },
    BOARD: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.techAdmin,
        ROLES.admin,
        ROLES.adminOfficer,
      ],
    },
  },

  // The client contacts alone, for an HLB person whose only role is employee.
  // The page the mail to clients leads to; the server decides per company.
  CLIENT_CONTACTS: {
    VIEW: {
      requiredRoles: [ROLES.client, ROLES.clientOfficer, ROLES.clientContact],
      requiredPermissions: [],
      bypassAuthCheck: false,
      // The link in the mail has to lead here, also through a sign-in.
      returnAfterLogin: true,
    },
  },

  // The role is common to every employee of every client: the login flag and
  // the server narrow it down.
  TEAM_CONTACTS: {
    VIEW: {
      requiredRoles: [ROLES.employee],
      requiredPermissions: [],
      bypassAuthCheck: false,
      requiredCompanyFlag: 'hasTeamContactsOnly',
    },
  },

  FLEET: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin, ROLES.fleetOfficer],
      requiredPermissions: [],
      bypassAuthCheck: false,
    },
  },

  OZ: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [],
      bypassAuthCheck: false,
    },
  },

  PAYROLL_AUTOMATION: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.payrollAutomationView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.payrollAutomationCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.payrollAutomationEdit],
      bypassAuthCheck: false,
    },
  },

  SMART_PARSER: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin, ROLES.admin],
      requiredPermissions: [PERMISSIONS.smartParserView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin, ROLES.admin],
      requiredPermissions: [PERMISSIONS.smartParserCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin, ROLES.admin],
      requiredPermissions: [PERMISSIONS.smartParserEdit],
      bypassAuthCheck: false,
    },
  },

  TIME_TRACKING: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.timeTrackingView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.timeTrackingCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.timeTrackingEdit],
      bypassAuthCheck: false,
    },
  },

  PROJECT: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.hrOfficer,
        ROLES.employee,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.projectView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.projectCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.clientOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.projectEdit],
      bypassAuthCheck: false,
    },
  },

  GROUP_REPORTING: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.techAdmin,
        ROLES.adminOfficer,
      ],
      requiredPermissions: [PERMISSIONS.groupReportingView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.groupReportingCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.groupReportingEdit],
      bypassAuthCheck: false,
    },
  },

  CLIENT_REPORTS: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.techAdmin,
        ROLES.adminOfficer,
      ],
      requiredPermissions: [PERMISSIONS.clientReportsView],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.clientReportsEdit],
      bypassAuthCheck: false,
    },
    // What the accountant published to the client ("My reports").
    MY: {
      requiredRoles: [ROLES.client, ROLES.clientOfficer, ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.clientReportsView],
      bypassAuthCheck: false,
    },
  },

  FINANCIAL_STATEMENTS: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.techAdmin,
        ROLES.adminOfficer,
      ],
      requiredPermissions: [PERMISSIONS.financialStatementsView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.financialStatementsCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [ROLES.superAdmin, ROLES.admin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.financialStatementsEdit],
      bypassAuthCheck: false,
    },
  },

  ANNOUNCEMENTS: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.announcementView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.announcementCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.announcementEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [ROLES.superAdmin, ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.announcementRemove],
      bypassAuthCheck: false,
    },
  },

  INVOICES: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.invoiceView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.invoiceCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.invoiceEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.invoiceRemove],
      bypassAuthCheck: false,
    },
  },

  E_INVOICES: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.eInvoiceView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.eInvoiceCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.eInvoiceEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.eInvoiceRemove],
      bypassAuthCheck: false,
    },
  },

  CUSTOMERS: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.customerView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.customerCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.customerEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.customerRemove],
      bypassAuthCheck: false,
    },
  },

  PRODUCTS: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.productView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.productCreate],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.productEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.productRemove],
      bypassAuthCheck: false,
    },
  },

  ARCHIVE: {
    VIEW: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.invoiceView, PERMISSIONS.eInvoiceView],
      bypassAuthCheck: false,
    },
    CREATE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [
        PERMISSIONS.invoiceCreate,
        PERMISSIONS.eInvoiceCreate,
      ],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [PERMISSIONS.invoiceEdit, PERMISSIONS.eInvoiceEdit],
      bypassAuthCheck: false,
    },
    REMOVE: {
      requiredRoles: [
        ROLES.superAdmin,
        ROLES.admin,
        ROLES.client,
        ROLES.eInvoiceOfficer,
        ROLES.techAdmin,
      ],
      requiredPermissions: [
        PERMISSIONS.invoiceRemove,
        PERMISSIONS.eInvoiceRemove,
      ],
      bypassAuthCheck: false,
    },
  },

  ACCOUNT: {
    VIEW: {
      requiredRoles: [],
      requiredPermissions: [],
      bypassAuthCheck: true,
    },
    EDIT: {
      requiredRoles: [],
      requiredPermissions: [],
      bypassAuthCheck: true,
    },
    REMOVE: {
      requiredRoles: [],
      requiredPermissions: [],
      bypassAuthCheck: true,
    },
  },

  CONTACT_US: {
    VIEW: {
      requiredRoles: [],
      requiredPermissions: [],
      bypassAuthCheck: true,
    },
  },

  SUPPORT_TICKETS: {
    VIEW: {
      requiredRoles: [ROLES.superAdmin],
      requiredPermissions: [PERMISSIONS.supportView],
      bypassAuthCheck: false,
    },
    EDIT: {
      requiredRoles: [ROLES.superAdmin],
      requiredPermissions: [PERMISSIONS.supportEdit],
      bypassAuthCheck: false,
    },
  },

  MY_TICKETS: {
    VIEW: {
      requiredRoles: [],
      requiredPermissions: [],
      bypassAuthCheck: true,
    },
  },

  TECH_ADMIN: {
    VIEW: {
      requiredRoles: [ROLES.techAdmin],
      requiredPermissions: [PERMISSIONS.techAdminView],
      bypassAuthCheck: false,
    },
  },

  ACCESS_DENIED: {
    requiredRoles: [],
    requiredPermissions: [],
    bypassAuthCheck: true,
  },

  NOT_FOUND: {
    requiredRoles: [],
    requiredPermissions: [],
    bypassAuthCheck: true,
  },
});

const SUPPORT_ATTACHMENTS_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/quicktime', // .mov
  'video/x-msvideo', // .avi
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export {
  ROUTES,
  OPERATION,
  ROLES,
  PERMISSIONS,
  ROLES_PERMISSIONS,
  SUPPORT_ATTACHMENTS_TYPES,
};
